/**
 * IndexedDB 유틸리티
 * 로컬 마이포스트 데이터를 브라우저에 영구 저장
 */

import { devLog } from './devLog';

const DB_NAME = 'LocalPostDB';
const DB_VERSION = 1;
const STORE_NAME = 'posts';

/**
 * IndexedDB 열기
 * @returns {Promise<IDBDatabase>}
 */
const openDB = () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    
    request.onsuccess = () => {
      resolve(request.result);
    };
    
    request.onerror = () => {
      console.error('[IndexedDB] 열기 실패:', request.error);
      reject(request.error);
    };
    
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      
      // Object Store가 없으면 생성
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'key' });
        devLog('[IndexedDB] Object Store 생성:', STORE_NAME);
      }
    };
  });
};

/**
 * IndexedDB에 데이터 저장
 * @param {string} key - 저장할 키
 * @param {any} data - 저장할 데이터
 * @returns {Promise<void>}
 */
export const saveToIndexedDB = async (key, data) => {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    
    await store.put({ key, data, timestamp: Date.now() });
    
    return new Promise((resolve, reject) => {
      tx.oncomplete = () => {
        devLog('[IndexedDB] 저장 완료:', key);
        resolve();
      };
      tx.onerror = () => {
        console.error('[IndexedDB] 저장 실패:', tx.error);
        reject(tx.error);
      };
    });
  } catch (error) {
    console.error('[IndexedDB] saveToIndexedDB 오류:', error);
    throw error;
  }
};

/**
 * IndexedDB에서 데이터 로드
 * @param {string} key - 로드할 키
 * @returns {Promise<any|null>} 저장된 데이터 또는 null
 */
export const loadFromIndexedDB = async (key) => {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    
    return new Promise((resolve, reject) => {
      const request = store.get(key);
      
      request.onsuccess = () => {
        if (request.result) {
          devLog('[IndexedDB] 로드 완료:', key);
          resolve(request.result.data);
        } else {
          devLog('[IndexedDB] 데이터 없음:', key);
          resolve(null);
        }
      };
      
      request.onerror = () => {
        console.error('[IndexedDB] 로드 실패:', request.error);
        reject(request.error);
      };
    });
  } catch (error) {
    console.error('[IndexedDB] loadFromIndexedDB 오류:', error);
    throw error;
  }
};

/**
 * IndexedDB에서 데이터 삭제
 * @param {string} key - 삭제할 키
 * @returns {Promise<void>}
 */
export const deleteFromIndexedDB = async (key) => {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    
    await store.delete(key);
    
    return new Promise((resolve, reject) => {
      tx.oncomplete = () => {
        devLog('[IndexedDB] 삭제 완료:', key);
        resolve();
      };
      tx.onerror = () => {
        console.error('[IndexedDB] 삭제 실패:', tx.error);
        reject(tx.error);
      };
    });
  } catch (error) {
    console.error('[IndexedDB] deleteFromIndexedDB 오류:', error);
    throw error;
  }
};

/**
 * IndexedDB 전체 초기화
 * @returns {Promise<void>}
 */
export const clearIndexedDB = async () => {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    
    await store.clear();
    
    return new Promise((resolve, reject) => {
      tx.oncomplete = () => {
        devLog('[IndexedDB] 전체 초기화 완료');
        resolve();
      };
      tx.onerror = () => {
        console.error('[IndexedDB] 초기화 실패:', tx.error);
        reject(tx.error);
      };
    });
  } catch (error) {
    console.error('[IndexedDB] clearIndexedDB 오류:', error);
    throw error;
  }
};

